import React, { useState, useEffect } from "react";
import { Modal, Button, Dropdown, message, Table, Empty } from "antd";
import { FileOutlined, FilePdfOutlined, FileExcelOutlined } from "@ant-design/icons";
import { formatDateAndTime } from "../../Utils/Imports/date-utils";
import { MinusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import dayjs from "dayjs";

const ReprintLogsModal = ({
  isModalOpen,
  handleCancel,
  reprintLogs,
  transactionId,
  reprintCount,
}) => {
  const { t } = useTranslation();
  // CSV export state
  const csvHeaders = [
    {
      label: t("transactionScreen.table.transactionId"),
      key: "transactionId",
    },
    {
      label: t("reprintLogsModal.table.reprintDate"),
      key: "reprintDate",
    },
    {
      label: t("reprintLogsModal.table.name"),
      key: "name",
    },
    {
      label: t("reprintLogsModal.table.surname"),
      key: "surname",
    },
    {
      label: t("reprintLogsModal.table.email"),
      key: "email",
    },
    {
      label: t("reprintLogsModal.table.kioskName"),
      key: "kioskName",
    },
  ];
  
  const [csvData, setCsvData] = useState([]);
  // Update CSV data when reprintLogs change
  useEffect(() => {
    const formattedData = (reprintLogs || []).map((item) => ({
      transactionId: transactionId,
      reprintDate: item?.created ? formatDateAndTime(item.created) : "-",
      name: item?.user?.name || "-",
      surname: item?.user?.surname || "-",
      email: item?.user?.username || "-",
      kioskName: item?.kiosk?.name || "-",
    }));
    setCsvData(formattedData);
  }, [reprintLogs, transactionId]);

  // Export to PDF function
  const exportToPDF = () => {
    try {
      const doc = new jsPDF('landscape');
      
      const title = `${t("reprintLogsModal.title")} - ${t("transactionScreen.table.transactionId")}: ${transactionId}`;
      const pageWidth = doc.internal.pageSize.getWidth();
      const textWidth = doc.getTextWidth(title);
      const xPosition = (pageWidth - textWidth) / 2;
      
      doc.setFontSize(16);
      doc.text(title, xPosition, 20);
      
      const tableColumn = csvHeaders.map((col) => col.label);
      const tableRows = csvData.map((row) => csvHeaders.map((col) => row[col.key]));
      
      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 30,
      });

      const filename = `Reprint-Logs-Transaction-${transactionId}-${dayjs().format('DD-MM-YYYY-hh-mm')}.pdf`;
      doc.save(filename);
    } catch (error) {
      message.error(t("drawerButtons.errorExportPdf"));
    }
  };

  // Generate CSV function
  const handleGenerateCsv = (event, done) => {
    const promise = async () => {
      // CSV data is already set in useEffect
    };
    promise().then(done());
  };

  // Export menu items
  const exportMenuItems = [
    {
      key: "1",
      label: t("drawerButtons.exportPdf"),
      icon: <FilePdfOutlined />,
      onClick: exportToPDF,
    },
    {
      type: "divider",
    },
    {
      key: "2",
      label: (
        <CSVLink
          data={csvData}
          headers={csvHeaders}
          asyncOnClick={true}
          onClick={handleGenerateCsv}
          filename={`Reprint-Logs-Transaction-${transactionId}-${dayjs().format('DD-MM-YYYY-hh-mm')}.csv`}
        >
          {t("drawerButtons.exportCsv")}
        </CSVLink>
      ),
      icon: <FileExcelOutlined />,
    },
  ];    const reprintLogColumns = [
    {
      title: t("reprintLogsModal.table.reprintDate"),
      key: "reprintDate",
      render: (record) => {
        return record?.created ? (
          formatDateAndTime(record.created)
        ) : (
          <MinusOutlined />
        );
      },
      sorter: (a, b) => {
        const dateA = new Date(a.created);
        const dateB = new Date(b.created);
        return dateA.getTime() - dateB.getTime();
      },
      show: true,
      width: 180,
    },
    {
      title: t("reprintLogsModal.table.name"),
      key: "name",
      render: (record) => {
        return <span>{record?.user?.name || "-"}</span>;
      },
      sorter: (a, b) => (a.user?.name || "").localeCompare(b.user?.name || ""),
      show: true,
      width: 120,
    },
    {
      title: t("reprintLogsModal.table.surname"),
      key: "surname",
      render: (record) => {
        return <span>{record?.user?.surname || "-"}</span>;
      },
      sorter: (a, b) => (a.user?.surname || "").localeCompare(b.user?.surname || ""),
      show: true,
      width: 120,
    },
    {
      title: t("reprintLogsModal.table.email"),
      key: "email",
      render: (record) => {
        return <span style={{ wordBreak: 'break-word' }}>{record?.user?.username || "-"}</span>;
      },
      sorter: (a, b) => (a.user?.username || "").localeCompare(b.user?.username || ""),
      show: true,
      width: 200,
      ellipsis: true,
    },
    {
      title: t("reprintLogsModal.table.kioskName"),
      key: "kioskName",
      render: (record) => {
        return <span>{record?.kiosk?.name || "-"}</span>;
      },
      sorter: (a, b) => (a.kiosk?.name || "").localeCompare(b.kiosk?.name || ""),
      show: true,
      width: 150,
      ellipsis: true,
    },
  ];return (
    <Modal
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '40px' }}>
          <span>
            {`${t("reprintLogsModal.title")} - ${t("transactionScreen.table.transactionId")}: ${transactionId} (${reprintCount || 0} ${reprintCount === 1 ? t("reprintLogsModal.reprint") : t("reprintLogsModal.reprints")})`}
          </span>
          <Dropdown menu={{ items: exportMenuItems }} placement="bottomLeft">
            <Button
              icon={<FileOutlined />}
              size="small"
              style={{
                marginRight: 16,
              }}
            >
              {t("drawerButtons.export")}
            </Button>
          </Dropdown>
        </div>
      }      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      width={1000}
    >      <div style={{ overflowX: 'auto' }}>
        <Table
          size="small"
          columns={reprintLogColumns.filter((column) => column.show)}
          dataSource={reprintLogs || []}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          scroll={{ x: 'max-content' }}
          rowKey={(record, index) => record.id || index}          locale={{
            emptyText: <Empty description={t("noData")} />,
          }}
        />
      </div>
    </Modal>
  );
};

export default ReprintLogsModal;
